<aside class="flex flex-col w-60 h-full shrink-0" style="background: var(--nexus-panel)">
  <!-- Logo - no right border, seamless with topbar -->
  <div
    class="flex items-center gap-3 px-4 h-14 border-b shrink-0"
    style="border-color: var(--nexus-border)"
  >
    <img
      src="/nexus-logo.webp"
      alt="Nexus"
      class="w-9 h-9 object-contain"
      style="filter: drop-shadow(0 0 8px rgba(0, 229, 255, 0.45))"
    />
    <div>
      <div class="flex items-center gap-2 text-xs font-bold tracking-widest uppercase" style="color: var(--nexus-cyan)">
        WESTYX NEXUS
        @if (authService.isPlatformAdmin()) {
          <span class="text-[9px] font-bold px-1 py-0.5 rounded"
                style="background:rgba(0,200,223,0.15);color:var(--nexus-cyan);letter-spacing:0.05em;text-transform:none;font-weight:700">
            PA
          </span>
        }
      </div>
      <div class="text-[10px]" style="color: var(--nexus-text-dim)">Admin Console</div>
    </div>
  </div>

  <!-- Nav + bottom: carries the right border -->
  <div
    class="flex flex-col flex-1 border-r overflow-hidden"
    style="border-color: var(--nexus-border)"
  >
    <!-- Navigation -->
    <nav class="flex-1 py-3 overflow-y-auto">

      <!-- ── TENANT sections (hidden for platform admin) ── -->
      @if (!authService.isPlatformAdmin()) {

        <!-- Dashboard (everyone) - no section label -->
        @if (!activeTenantDeleted()) {
          <div class="px-3 mb-1">
            <a routerLink="/dashboard" routerLinkActive="nav-active"
               class="nav-item flex items-center gap-2.5 px-2 py-2 rounded-md text-xs font-medium transition-all mb-0.5">
              <lucide-icon [img]="DashboardIcon" class="w-3.5 h-3.5 shrink-0" />
              Dashboard
            </a>
          </div>
        }

        <!-- COMPANY -->
        <div class="px-3 mt-3 mb-1">
          <span class="nx-label px-2">Company</span>
          <a routerLink="/me/tenants" routerLinkActive="nav-active"
             class="nav-item flex items-center gap-2.5 px-2 py-2 rounded-md text-xs font-medium transition-all mb-0.5">
            <lucide-icon [img]="TenantsIcon" class="w-3.5 h-3.5 shrink-0" />
            Tenants
            @if (tenantCount() > 1) {
              <span class="ml-auto text-[10px] opacity-50">{{ tenantCount() }}</span>
            }
          </a>
          @if (!activeTenantDeleted()) {
            <a routerLink="/projects" routerLinkActive="nav-active"
               class="nav-item flex items-center gap-2.5 px-2 py-2 rounded-md text-xs font-medium transition-all mb-0.5">
              <lucide-icon [img]="FolderIcon" class="w-3.5 h-3.5 shrink-0" />
              Projects
            </a>
            @if (canSeeMembers() && tenantMembersLink(); as membersLink) {
              <a [routerLink]="membersLink" routerLinkActive="nav-active"
                 class="nav-item flex items-center gap-2.5 px-2 py-2 rounded-md text-xs font-medium transition-all mb-0.5">
                <lucide-icon [img]="MembersIcon" class="w-3.5 h-3.5 shrink-0" />
                Users
              </a>
            }
            @if (canSeeAuditLogs()) {
              <a routerLink="/audit-logs" routerLinkActive="nav-active"
                 class="nav-item flex items-center gap-2.5 px-2 py-2 rounded-md text-xs font-medium transition-all mb-0.5">
                <lucide-icon [img]="AuditLogsIcon" class="w-3.5 h-3.5 shrink-0" />
                Audit Logs
              </a>
            }
            @if (canSeeSecurity()) {
              <a routerLink="/security" routerLinkActive="nav-active"
                 class="nav-item flex items-center gap-2.5 px-2 py-2 rounded-md text-xs font-medium transition-all mb-0.5">
                <lucide-icon [img]="SecurityIcon" class="w-3.5 h-3.5 shrink-0" />
                Security
              </a>
            }
          }
          @if (canSeeSettings() && tenantSettingsLink(); as settingsLink) {
            <a [routerLink]="settingsLink" routerLinkActive="nav-active"
               class="nav-item flex items-center gap-2.5 px-2 py-2 rounded-md text-xs font-medium transition-all mb-0.5">
              <lucide-icon [img]="SettingsIcon" class="w-3.5 h-3.5 shrink-0" />
              Settings
            </a>
          }
          @if (activeTenantDeleted()) {
            <p class="px-2 mt-1 text-[10px] leading-snug" style="color:var(--nexus-red)">
              This tenant is deleted. Billing stays open so you can settle what is owed; restore it
              in Settings to get everything else back.
            </p>
          }
        </div>

        <!-- BILLING (billing_access or owner) - below Company -->
        <!-- Billing stays reachable on a soft-deleted tenant: a closing invoice is
             raised at deletion, and the owner must be able to settle it during the
             recovery window (docs/adr/0008). Plans is a new purchase, so it goes. -->
        @if (canSeeBilling()) {
          <div class="px-3 mt-3 mb-1">
            <span class="nx-label px-2">Billing</span>
            <a routerLink="/billing" routerLinkActive="nav-active"
               class="nav-item flex items-center gap-2.5 px-2 py-2 rounded-md text-xs font-medium transition-all mb-0.5">
              <lucide-icon [img]="BillingIcon" class="w-3.5 h-3.5 shrink-0" />
              Billing
            </a>
            @if (!activeTenantDeleted()) {
              <a routerLink="/pricing" routerLinkActive="nav-active"
                 class="nav-item flex items-center gap-2.5 px-2 py-2 rounded-md text-xs font-medium transition-all mb-0.5">
                <lucide-icon [img]="PlansIcon" class="w-3.5 h-3.5 shrink-0" />
                Plans
              </a>
            }
          </div>
        }

        <!-- HELP (everyone) -->
        <div class="px-3 mt-3 mb-1">
          <span class="nx-label px-2">Help</span>
          <a routerLink="/whats-new" routerLinkActive="nav-active"
             class="nav-item flex items-center gap-2.5 px-2 py-2 rounded-md text-xs font-medium transition-all mb-0.5">
            <lucide-icon [img]="BellIcon" class="w-3.5 h-3.5 shrink-0" />
            What's New
            @if (announcementsService.unreadCount() > 0) {
              <span class="ml-auto w-2 h-2 rounded-full shrink-0"
                    style="background: var(--nexus-cyan); box-shadow: 0 0 0 1.5px var(--nexus-panel)"></span>
            }
          </a>
          <a routerLink="/feedback" routerLinkActive="nav-active"
             class="nav-item flex items-center gap-2.5 px-2 py-2 rounded-md text-xs font-medium transition-all mb-0.5">
            <lucide-icon [img]="MyFeedbackIcon" class="w-3.5 h-3.5 shrink-0" />
            My Feedback
            @if (feedbackService.userUnreadCount() > 0) {
              <span class="ml-auto w-2 h-2 rounded-full shrink-0"
                    style="background: var(--nexus-cyan); box-shadow: 0 0 0 1.5px var(--nexus-panel)"></span>
            }
          </a>
          <a [href]="docsUrl" target="_blank" rel="noopener"
             class="nav-item flex items-center gap-2.5 px-2 py-2 rounded-md text-xs font-medium transition-all mb-0.5">
            <lucide-icon [img]="HelpIcon" class="w-3.5 h-3.5 shrink-0" />
            Help <span class="ml-1 opacity-60">↗</span>
          </a>
        </div>

      } <!-- end tenant sections -->

      <!-- ── PLATFORM ADMIN sections ── -->
      @if (authService.isPlatformAdmin()) {

        <!-- PLATFORM -->
        <div class="px-3 mb-1">
          <span class="nx-label px-2">Platform</span>
          <a routerLink="/platform-admin/platform" routerLinkActive="nav-active"
             class="nav-item flex items-center gap-2.5 px-2 py-2 rounded-md text-xs font-medium transition-all mb-0.5">
            <lucide-icon [img]="DashboardIcon" class="w-3.5 h-3.5 shrink-0" />
            Overview
          </a>
          <a routerLink="/platform/alerts" routerLinkActive="nav-active"
             class="nav-item flex items-center gap-2.5 px-2 py-2 rounded-md text-xs font-medium transition-all mb-0.5">
            <lucide-icon [img]="BellIcon" class="w-3.5 h-3.5 shrink-0" />
            Alerts
          </a>
          <a routerLink="/platform-admin/tiers" routerLinkActive="nav-active"
             class="nav-item flex items-center gap-2.5 px-2 py-2 rounded-md text-xs font-medium transition-all mb-0.5">
            <lucide-icon [img]="PlansIcon" class="w-3.5 h-3.5 shrink-0" />
            Tiers
          </a>
          <a routerLink="/platform-admin/promo-codes" routerLinkActive="nav-active"
             class="nav-item flex items-center gap-2.5 px-2 py-2 rounded-md text-xs font-medium transition-all mb-0.5">
            <lucide-icon [img]="PromoCodesIcon" class="w-3.5 h-3.5 shrink-0" />
            Promo codes
          </a>
          <a routerLink="/platform-admin/platform/environments" routerLinkActive="nav-active"
             class="nav-item flex items-center gap-2.5 px-2 py-2 rounded-md text-xs font-medium transition-all mb-0.5">
            <lucide-icon [img]="EnvironmentsIcon" class="w-3.5 h-3.5 shrink-0" />
            Environments
          </a>
          <a routerLink="/platform-admin/announcements" routerLinkActive="nav-active"
             class="nav-item flex items-center gap-2.5 px-2 py-2 rounded-md text-xs font-medium transition-all mb-0.5">
            <lucide-icon [img]="MegaphoneIcon" class="w-3.5 h-3.5 shrink-0" />
            Announcements
          </a>
          <a routerLink="/platform-admin/feedback" routerLinkActive="nav-active"
             class="nav-item flex items-center gap-2.5 px-2 py-2 rounded-md text-xs font-medium transition-all mb-0.5">
            <lucide-icon [img]="FeedbackAdminIcon" class="w-3.5 h-3.5 shrink-0" />
            Feedback
            @if (platformSettingsService.adminUnreadCount() > 0) {
              <span class="ml-auto w-2 h-2 rounded-full shrink-0"
                    style="background: var(--nexus-cyan); box-shadow: 0 0 0 1.5px var(--nexus-panel)"></span>
            }
          </a>
          <a routerLink="/platform-admin/platform-settings" routerLinkActive="nav-active"
             class="nav-item flex items-center gap-2.5 px-2 py-2 rounded-md text-xs font-medium transition-all mb-0.5">
            <lucide-icon [img]="PlatformSettingsIcon" class="w-3.5 h-3.5 shrink-0" />
            Platform Settings
            @if (platformSettingsService.pendingRestart()) {
              <span class="ml-auto w-2 h-2 rounded-full shrink-0"
                    style="background: var(--nexus-yellow); box-shadow: 0 0 0 1.5px var(--nexus-panel)"
                    title="Settings pending restart"></span>
            }
          </a>
        </div>

        <!-- ADMINISTRATION -->
        <div class="px-3 mt-3 mb-1">
          <span class="nx-label px-2">Administration</span>
          <a routerLink="/platform-admin/tenants" routerLinkActive="nav-active"
             class="nav-item flex items-center gap-2.5 px-2 py-2 rounded-md text-xs font-medium transition-all mb-0.5">
            <lucide-icon [img]="TenantsIcon" class="w-3.5 h-3.5 shrink-0" />
            Tenants
          </a>
          <a routerLink="/projects" routerLinkActive="nav-active"
             class="nav-item flex items-center gap-2.5 px-2 py-2 rounded-md text-xs font-medium transition-all mb-0.5">
            <lucide-icon [img]="FolderIcon" class="w-3.5 h-3.5 shrink-0" />
            Projects
          </a>
          <a routerLink="/users" routerLinkActive="nav-active"
             class="nav-item flex items-center gap-2.5 px-2 py-2 rounded-md text-xs font-medium transition-all mb-0.5">
            <lucide-icon [img]="MembersIcon" class="w-3.5 h-3.5 shrink-0" />
            Users
          </a>
          <a routerLink="/platform-admin/access-requests" routerLinkActive="nav-active"
             class="nav-item flex items-center gap-2.5 px-2 py-2 rounded-md text-xs font-medium transition-all mb-0.5"
             style="color: var(--nexus-yellow)">
            <lucide-icon [img]="InboxIcon" class="w-3.5 h-3.5 shrink-0" style="color: var(--nexus-yellow)" />
            Access Requests
            @if (platformAdminService.unreadCount() > 0) {
              <span class="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                    style="background: rgba(245,158,11,0.15); color:var(--badge-amber-fg); border: 1px solid rgba(245,158,11,0.3)">
                {{ platformAdminService.unreadCount() }}
              </span>
            }
          </a>
        </div>

        <!-- HELP (platform admin) -->
        <div class="px-3 mt-3 mb-1">
          <span class="nx-label px-2">Help</span>
          <a [href]="docsUrl" target="_blank" rel="noopener"
             class="nav-item flex items-center gap-2.5 px-2 py-2 rounded-md text-xs font-medium transition-all mb-0.5">
            <lucide-icon [img]="HelpIcon" class="w-3.5 h-3.5 shrink-0" />
            Help <span class="ml-1 opacity-60">↗</span>
          </a>
        </div>

      } <!-- end platform admin sections -->

    </nav>

    <!-- Active service (only shown when one is selected) -->
    @if (settingsService.activeService()) {
      <div class="px-3 pt-2 border-t" style="border-color: var(--nexus-border)">
        <div
          class="flex items-center gap-2 px-2 py-1.5 rounded"
          style="background: var(--nexus-cyan-dim); border: 1px solid var(--nexus-border-glow)"
        >
          <span class="nx-dot-cyan"></span>
          <span class="text-[11px] font-mono truncate" style="color: var(--nexus-cyan)">
            {{ settingsService.activeService()!.name }}
          </span>
        </div>
      </div>
    }

    <!-- Send Feedback (tenant only, pinned above version) -->
    @if (!authService.isPlatformAdmin()) {
      <div class="px-3 py-2 border-t" style="border-color: var(--nexus-border)">
        <button
          class="flex items-center gap-2 px-2 py-1.5 rounded-md text-xs font-semibold w-full text-left transition-all"
          style="color: var(--nexus-yellow); background: rgba(245,158,11,0.15); border: 1px solid rgba(245,158,11,0.55)"
          title="Send Feedback"
          (click)="openFeedback()"
        >
          <lucide-icon [img]="FeedbackIcon" class="w-3.5 h-3.5 shrink-0" style="color: var(--nexus-yellow)" />
          Send Feedback
        </button>
      </div>
    }

    <!-- Version display -->
    <div class="px-5 py-2 border-t flex flex-col gap-0.5" style="border-color: var(--nexus-border)">
      <div class="text-[10px]" style="color: var(--nexus-text-dim)">
        <span style="color: var(--nexus-text-muted)">FE</span>
        {{ versionService.frontendVersion }}
      </div>
      @if (versionService.backendVersion(); as bv) {
        <div
          class="text-[10px]"
          style="color: var(--nexus-text-dim)"
          [title]="'Built: ' + bv.build_time"
        >
          <span style="color: var(--nexus-text-muted)">BE</span>
          {{ bv.version }}
          @if (!bv.version.startsWith('dev-')) {
            <span style="opacity: 0.4">({{ bv.commit }})</span>
          }
        </div>
      } @else if (versionService.backendError()) {
        <div class="text-[10px]" style="color: var(--nexus-text-dim)">
          <span style="color: var(--nexus-text-muted)">BE</span>
          <span style="opacity: 0.4">unavailable</span>
        </div>
      }
    </div>
  </div>
  <!-- /border-r wrapper -->
</aside>

@if (feedbackOpen()) {
  <app-feedback-modal #feedbackModal />
}
